import { useEffect, useRef, useState } from 'react'
import {
  Check, ChevronRight, CircleAlert, Copy, ExternalLink, LoaderCircle, Repeat, RotateCcw, Sparkles, X,
} from 'lucide-react'
import type { Bericht, BeurtStand, Bron, Stap, Voorstel } from '../../../gedeeld/types'
import { standTekst } from '../../lib/beurt'
import { Markdown, veiligeUrl } from '../../lib/markdown'
import { datumTijd, duur, kloktijd, meervoud } from '../../lib/opmaak'
import { meld } from '../../lib/staat'
import { BestandKaart, MeegestuurdeBestanden } from './Bestanden'
import { onderwerpIcoon } from './iconen'
import VoorstelKaart from './VoorstelKaart'

/* ===========================================================================
 *  De berichten in een gesprek
 *
 *  Jouw vraag rechts in een bel, het antwoord links zonder bel -- zoals in
 *  elke chat-app, omdat een antwoord vaak een tabel of een lijst is en een
 *  bel daar alleen ruimte van afsnoept.
 *
 *  Onder een antwoord: hoe Cowork eraan kwam (de stappen, ingeklapt), waar
 *  het op steunt (de bronnen), en wat hij voorstelt te doen (de kaarten).
 *  Wat de AI wil DOEN staat nooit in de tekst als "ik heb het gedaan": het
 *  staat in een kaart, met de velden open, en pas jouw klik voert het uit.
 * =========================================================================== */

export function CoworkAvatar({ denkt }: { denkt?: boolean }) {
  return (
    <span className={`cw-avatar ${denkt ? 'denkt' : ''}`} aria-hidden>
      <Sparkles size={15} />
    </span>
  )
}

/* ------------------------------------------------------------------ *
 *  Jij
 * ------------------------------------------------------------------ */

export function GebruikerBericht({ b, verstuurt }: { b: Bericht; verstuurt?: boolean }) {
  return (
    <div className={`bericht gebruiker ${verstuurt ? 'verstuurt' : ''}`}>
      {b.automatisch && <span className="bericht-label"><Repeat size={12} /> Terugkerende taak</span>}
      <div className="bel">
        <Markdown tekst={b.tekst} />
      </div>
      {!!b.bestanden?.length && <MeegestuurdeBestanden bestanden={b.bestanden} />}
      <span className="bericht-tijd" title={datumTijd(b.at)}>{verstuurt ? 'Versturen…' : kloktijd(b.at)}</span>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 *  Cowork
 * ------------------------------------------------------------------ */

const zonderBeweging = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches } catch { return false }
}

/**
 * Het antwoord kort "uitschrijven" als het net binnenkomt.
 *
 * Er komt geen stroom van woorden van de pc op kantoor: het antwoord is er
 * in één keer. Het in een seconde of anderhalf zichtbaar laten worden maakt
 * dat je ziet wát er nieuw is, en het voelt minder als een blok dat uit de
 * lucht valt. Langer niet: dan wacht je op een animatie in plaats van op
 * het antwoord. Wie minder beweging wil, krijgt het meteen.
 */
function useOnthulling(tekst: string, aan: boolean, opKlaar: () => void): string {
  const [n, setN] = useState(() => (aan && !zonderBeweging() ? 0 : tekst.length))
  const klaarRef = useRef(opKlaar)
  klaarRef.current = opKlaar

  useEffect(() => {
    if (!aan) { setN(tekst.length); return }
    if (zonderBeweging() || tekst.length < 24) { setN(tekst.length); klaarRef.current(); return }
    const lengte = tekst.length
    const totaal = Math.min(1500, Math.max(900, lengte * 2.2))
    const begin = performance.now()
    let raam = 0
    const stap = (nu: number) => {
      const p = Math.min(1, (nu - begin) / totaal)
      /* Eerst vlot, aan het eind rustiger: dat leest als schrijven. */
      const z = 1 - Math.pow(1 - p, 2.2)
      let tot = Math.ceil(lengte * z)
      /* Op een woordgrens afbreken, niet halverwege een woord. */
      const spatie = tekst.indexOf(' ', tot)
      if (spatie !== -1 && spatie - tot < 12) tot = spatie
      setN(Math.min(lengte, tot))
      if (p < 1) raam = requestAnimationFrame(stap)
      else klaarRef.current()
    }
    raam = requestAnimationFrame(stap)
    return () => cancelAnimationFrame(raam)
  }, [aan, tekst])

  if (!aan || n >= tekst.length) return tekst
  return tekst.slice(0, n)
}

export function AssistentBericht({
  b, laatste, onthul, onOnthuld, onOpnieuw, bezig, onVoorstel,
}: {
  b: Bericht
  laatste: boolean
  /** Net binnengekomen: even uitschrijven. */
  onthul: boolean
  onOnthuld: () => void
  onOpnieuw: () => void
  /** Loopt er een vraag? Dan kan "Opnieuw" niet. */
  bezig: boolean
  onVoorstel: (v: Voorstel) => void
}) {
  const tekst = useOnthulling(b.tekst, onthul, onOnthuld)
  const schrijft = onthul && tekst.length < b.tekst.length
  const [gekopieerd, setGekopieerd] = useState(false)

  async function kopieer() {
    try {
      await navigator.clipboard.writeText(b.tekst)
      setGekopieerd(true)
      setTimeout(() => setGekopieerd(false), 1600)
    } catch {
      meld('Kopiëren lukte niet; selecteer de tekst en kopieer hem zelf.', 'fout')
    }
  }

  if (b.fout) {
    return (
      <div className="bericht assistent fout">
        <CoworkAvatar />
        <div className="bericht-lijf">
          <div className="fout-vak" role="alert">
            <CircleAlert size={16} />
            <div className="vul">
              <Markdown tekst={b.tekst || 'Er kwam geen antwoord.'} />
            </div>
          </div>
          {laatste && (
            <div className="bericht-acties zichtbaar">
              <button type="button" className="actie-knop tekst" onClick={onOpnieuw} disabled={bezig}>
                <RotateCcw size={14} /> Opnieuw proberen
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="bericht assistent">
      <CoworkAvatar />
      <div className="bericht-lijf">
        {b.automatisch && <span className="bericht-label"><Repeat size={12} /> Terugkerende taak</span>}
        {!!b.stappen?.length && <Stappen stappen={b.stappen} duurMs={b.duurMs} />}
        <div className={`antwoord ${schrijft ? 'schrijft' : ''}`} aria-busy={schrijft}>
          <Markdown tekst={tekst} />
        </div>

        {!schrijft && (
          <>
            {!!b.voorstellen?.length && (
              <div className="voorstellen">
                {b.voorstellen.map((v) => <VoorstelKaart key={v.id} berichtId={b.id} voorstel={v} onVoorstel={onVoorstel} />)}
              </div>
            )}
            {!!b.bestanden?.length && (
              <div className="gemaakt" aria-label="Gemaakte bestanden">
                {b.bestanden.map((x) => <BestandKaart key={x.id} b={x} />)}
              </div>
            )}
            {!!b.bronnen?.length && <Bronnen bronnen={b.bronnen} />}
            <div className={`bericht-acties ${laatste ? 'zichtbaar' : ''}`}>
              <button type="button" className="actie-knop" onClick={() => void kopieer()}
                aria-label={gekopieerd ? 'Gekopieerd' : 'Kopiëren'} title={gekopieerd ? 'Gekopieerd' : 'Kopiëren'}>
                {gekopieerd ? <Check size={15} /> : <Copy size={15} />}
              </button>
              {laatste && (
                <button type="button" className="actie-knop" onClick={onOpnieuw} disabled={bezig}
                  aria-label="Opnieuw laten maken" title="Opnieuw laten maken">
                  <RotateCcw size={15} />
                </button>
              )}
              <span className="bericht-meta" title={datumTijd(b.at)}>
                {kloktijd(b.at)}
                {b.duurMs ? ` · ${duur(b.duurMs)}` : ''}
                {b.model ? ` · ${b.model}` : ''}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 *  Hoe Cowork eraan kwam
 * ------------------------------------------------------------------ */

function Stappen({ stappen, duurMs }: { stappen: Stap[]; duurMs?: number | null }) {
  const [open, setOpen] = useState(false)
  const fouten = stappen.filter((s) => s.status === 'fout').length
  return (
    <div className={`stappen-blok ${open ? 'open' : ''}`}>
      <button type="button" className="stappen-kop" aria-expanded={open} onClick={() => setOpen((x) => !x)}>
        <ChevronRight size={14} className="stappen-pijl" />
        <span>{meervoud(stappen.length, 'stap', 'stappen')}{duurMs ? ` · ${duur(duurMs)}` : ''}</span>
        {fouten > 0 && <span className="stappen-fout">· {fouten} lukte niet</span>}
      </button>
      {open && <StappenLijst stappen={stappen} />}
    </div>
  )
}

export function StappenLijst({ stappen, live }: { stappen: Stap[]; live?: boolean }) {
  return (
    <ol className={`stappen ${live ? 'live' : ''}`}>
      {stappen.map((s) => (
        <li key={s.id} className={`stap ${s.status}`}>
          <span className="stap-icoon" aria-hidden>{onderwerpIcoon(`${s.tool} ${s.label}`, 14)}</span>
          <span className="stap-tekst">
            <span className="stap-label">{s.label}</span>
            {s.samenvatting && <span className="stap-sam">{s.samenvatting}</span>}
          </span>
          {s.ms != null && s.status !== 'bezig' && <span className="stap-ms">{duur(s.ms)}</span>}
          <span className="stap-stand" aria-label={s.status === 'bezig' ? 'Bezig' : s.status === 'klaar' ? 'Klaar' : 'Mislukt'}>
            {s.status === 'bezig' ? <LoaderCircle size={13} className="draai" />
              : s.status === 'klaar' ? <Check size={13} /> : <X size={13} />}
          </span>
        </li>
      ))}
    </ol>
  )
}

/* ------------------------------------------------------------------ *
 *  Waar het op steunt
 * ------------------------------------------------------------------ */

function Bronnen({ bronnen }: { bronnen: Bron[] }) {
  return (
    <div className="bronnen" aria-label="Bronnen">
      {bronnen.map((b, i) => {
        const href = veiligeUrl(b.link)
        const inhoud = (
          <>
            <span className="bron-icoon" aria-hidden>{onderwerpIcoon(`${b.soort} ${b.titel}`, 13)}</span>
            <span className="bron-titel">{b.titel}</span>
            {b.bij && <span className="bron-bij">{b.bij}</span>}
            {href && <ExternalLink size={11} className="bron-uit" aria-hidden />}
          </>
        )
        const titel = [b.soort, b.titel, b.bij].filter(Boolean).join(' · ')
        return href
          ? <a key={i} className="bron" href={href} target="_blank" rel="noopener noreferrer" title={`${titel} (opent in een nieuw tabblad)`}>{inhoud}</a>
          : <span key={i} className="bron" title={titel}>{inhoud}</span>
      })}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 *  Terwijl Cowork bezig is
 * ------------------------------------------------------------------ */

export function Denkt({ stand }: { stand: BeurtStand }) {
  const [, tik] = useState(0)
  useEffect(() => {
    const t = setInterval(() => tik((x) => x + 1), 1000)
    return () => clearInterval(t)
  }, [])
  const { tekst, waarschuwing } = standTekst(stand)
  const seconden = Math.max(0, Math.round((Date.now() - stand.sinds) / 1000))

  return (
    <div className="bericht assistent denkt-bericht" aria-live="polite">
      <CoworkAvatar denkt />
      <div className="bericht-lijf">
        <div className={`denkt-regel ${waarschuwing ? 'waarschuwing' : ''}`}>
          {!waarschuwing && <span className="denkt-bolletjes" aria-hidden><i /><i /><i /></span>}
          {waarschuwing && <CircleAlert size={15} aria-hidden />}
          <span className="denkt-tekst">{tekst}</span>
          {seconden >= 2 && <span className="denkt-tijd" aria-hidden>{seconden} s</span>}
        </div>
        {!!stand.stappen.length && <StappenLijst stappen={stand.stappen} live />}
      </div>
    </div>
  )
}
